import { g as getRegisteredGlobal, r as registerGlobal, a as getAuthClient } from './index-b34238aa.js';
import { i as i18nProvider } from './index-5a73d8b4.js';
import { d as debounce } from './debounce-9c93e0a1.js';

var NotificationProviderScope;
(function (NotificationProviderScope) {
    NotificationProviderScope["Global"] = "global";
    NotificationProviderScope["MyTrumpf"] = "mytrumpf";
})(NotificationProviderScope || (NotificationProviderScope = {}));
var UxNotificationSeverityEnum;
(function (UxNotificationSeverityEnum) {
    UxNotificationSeverityEnum["Information"] = "Information";
    UxNotificationSeverityEnum["Warning"] = "Warning";
    UxNotificationSeverityEnum["Error"] = "Error";
})(UxNotificationSeverityEnum || (UxNotificationSeverityEnum = {}));
var UxNotificationStatusEnum;
(function (UxNotificationStatusEnum) {
    UxNotificationStatusEnum["Open"] = "open";
    UxNotificationStatusEnum["Closed"] = "closed";
    UxNotificationStatusEnum["Committed"] = "committed";
})(UxNotificationStatusEnum || (UxNotificationStatusEnum = {}));

const NAMESPACE = "trumpf.ux.notificationProvider";
let subscriberIncrement = 0;
const createSubscriberId = () => `notification-subscriber-${++subscriberIncrement}`;
const sortNotifications = (a, b) => b.timestamp - a.timestamp;
class NotificationProvider {
    constructor() {
        this.state = {
            counter: 0,
            error: null,
            loadMore: null,
            notifications: [],
            pending: false
        };
        this.subscribers = {};
        // ------------------------------------------------------------------------
        // Notification Backends
        // ------------------------------------------------------------------------
        this.registeredBackends = {};
        this.callBackendLoadMore = () => {
            Object.values(this.registeredBackends).forEach(({ scope }) => {
                const scopedNotificationProvider = getNotificationProvider(scope);
                if (typeof scopedNotificationProvider.loadMore === "function") {
                    scopedNotificationProvider.loadMore();
                }
            });
        };
        this.handleBackendProviderState = () => {
            let backendPending = false;
            let backendError = null;
            let backendNotifications = [];
            let backendTotalCounter = 0;
            let backendLoadMoreCounter = 0;
            Object.values(this.registeredBackends).forEach(({ scope }) => {
                const scopedNotificationProvider = getNotificationProvider(scope);
                if (scopedNotificationProvider.pending) {
                    backendPending = true;
                }
                if (scopedNotificationProvider.error !== null && backendError === null) {
                    backendError = scopedNotificationProvider.error;
                }
                if (typeof scopedNotificationProvider.loadMore === "function") {
                    backendLoadMoreCounter++;
                }
                backendNotifications = [...backendNotifications, ...scopedNotificationProvider.notifications];
                backendTotalCounter += Math.max(scopedNotificationProvider.notifications.length, scopedNotificationProvider.counter);
            });
            this.setNotifications(backendNotifications);
            this.setCounter(backendTotalCounter);
            this.setLoadMore(backendLoadMoreCounter > 0 ? this.callBackendLoadMore : null);
            this.setError(backendError);
            this.setPending(backendPending);
        };
        this.notifySubscribers = () => {
            Object.values(this.subscribers).forEach((callback) => {
                try {
                    callback(this.state);
                }
                catch (_a) {
                    // fail silently
                }
            });
        };
        this.notifySubscribersDebounced = debounce(this.notifySubscribers, 10);
    }
    /**
     * Counter used to inform user about the number of relevant notifications
     */
    get counter() {
        return this.state.counter;
    }
    setCounter(value) {
        if (value !== this.state.counter) {
            this.state = Object.assign(Object.assign({}, this.state), { counter: value });
            this.notifySubscribersDebounced();
        }
    }
    /**
     * Error if notifications could not be fetched
     */
    get error() {
        return this.state.error;
    }
    setError(value) {
        if (value !== this.state.error) {
            this.state = Object.assign(Object.assign({}, this.state), { error: value });
            this.notifySubscribersDebounced();
        }
    }
    /**
     * Notification list
     */
    get notifications() {
        return this.state.notifications;
    }
    setNotifications(value) {
        if (Array.isArray(value)) {
            this.state = Object.assign(Object.assign({}, this.state), { counter: value.length, notifications: value.sort(sortNotifications) });
            this.notifySubscribersDebounced();
        }
    }
    /**
     * Flag that indicates a running background process
     */
    get pending() {
        return this.state.pending;
    }
    setPending(value) {
        if (value !== this.state.pending) {
            this.state = Object.assign(Object.assign({}, this.state), { pending: value });
            this.notifySubscribersDebounced();
        }
    }
    addNotification(notification) {
        if (this.notifications.find((item) => item.id === notification.id) === undefined) {
            this.setNotifications([notification, ...this.notifications].sort(sortNotifications));
        }
    }
    removeNotification(notificationId) {
        if (this.notifications.find((item) => item.id === notificationId) !== undefined) {
            this.setNotifications(this.notifications.filter((item) => item.id !== notificationId));
        }
    }
    /**
     * Load more callback
     */
    get loadMore() {
        return this.state.loadMore;
    }
    setLoadMore(callback) {
        const loadMoreCallback = typeof callback === "function" ? callback : null;
        if (loadMoreCallback !== this.state.loadMore) {
            this.state = Object.assign(Object.assign({}, this.state), { loadMore: loadMoreCallback });
            this.notifySubscribersDebounced();
        }
    }
    registerBackend(scope, backend) {
        const scopedNotificationProvider = getNotificationProvider(scope);
        const subscriptionId = scopedNotificationProvider.subscribe(this.handleBackendProviderState);
        if (this.registeredBackends[scope]) {
            scopedNotificationProvider.unsubscribe(this.registeredBackends[scope].subscriptionId);
        }
        this.registeredBackends[scope] = {
            backend,
            scope,
            subscriptionId
        };
        backend.initProvider(scopedNotificationProvider);
    }
    // ------------------------------------------------------------------------
    // Subscriptions
    // ------------------------------------------------------------------------
    subscribe(callback) {
        const subscriberId = createSubscriberId();
        this.subscribers[subscriberId] = callback;
        return subscriberId;
    }
    unsubscribe(subscriberId) {
        delete this.subscribers[subscriberId];
    }
}
const getNotificationProvider = (name) => {
    const providerName = name || NotificationProviderScope.Global;
    const ns = `${NAMESPACE}.${providerName}`;
    let provider = getRegisteredGlobal(ns) || null;
    if (provider === null) {
        provider = new NotificationProvider();
        registerGlobal(provider, ns);
    }
    return provider;
};

const registerNotificationBackend = (backend, scope) => {
    const globalProvider = getNotificationProvider();
    if (!scope || scope === NotificationProviderScope.Global) {
        backend.initProvider(globalProvider);
    }
    else {
        globalProvider.registerBackend(scope, backend);
    }
};

const NOTIFICATION_API_BASEURLS = {
    dev: "https://notifications-d.my.trumpf.com",
    qa: "https://notifications-q.my.trumpf.com",
    prod: "https://notifications.my.trumpf.com"
};
const NOTIFICATION_API_VERSION = "2.0";
const NOTIFICATIONS_POLL_INTERVAL_DEFAULT = 10000;
const NOTIFICATIONS_POLL_INTERVAL_MIN = 5000;

const getApiBaseUrl = (stage) => {
    const baseUrl = NOTIFICATION_API_BASEURLS[stage] || NOTIFICATION_API_BASEURLS.prod;
    return `${baseUrl}/${NOTIFICATION_API_VERSION}`;
};
const mapSeverity = (severity) => {
    return ({
        INFORMATION: UxNotificationSeverityEnum.Information,
        WARNING: UxNotificationSeverityEnum.Warning,
        ERROR: UxNotificationSeverityEnum.Error
    }[`${severity}`.toUpperCase()] || UxNotificationSeverityEnum.Information);
};
const mapNotification = (notification, categories) => {
    const category = categories
        ? categories.find(({ name }) => name === notification.category)
        : null;
    const categoryTitle = (category === null || category === void 0 ? void 0 : category.title) || notification.category;
    return Object.assign(Object.assign({}, notification), { id: notification.id || `myt-${notification.timestamp}`, severity: mapSeverity(notification.severity), sourceName: notification.sourceName || categoryTitle });
};
const getRequestOptions = (token) => {
    return {
        headers: token
            ? {
                "X-JWT-Assertion": token
            }
            : {}
    };
};
const fetchNotifications = async ({ stage, locale, token }) => {
    const url = `${getApiBaseUrl(stage)}/notifications?locale=${locale.slice(0, 2)}`;
    return await fetch(url, getRequestOptions(token));
};
const fetchCategories = async ({ stage, locale, token }) => {
    const url = `${getApiBaseUrl(stage)}/categories?locale=${locale.slice(0, 2)}`;
    return await fetch(url, getRequestOptions(token));
};

var MyTrumpfEnvironmentEnum;
(function (MyTrumpfEnvironmentEnum) {
    MyTrumpfEnvironmentEnum["Development"] = "dev";
    MyTrumpfEnvironmentEnum["Production"] = "prod";
    MyTrumpfEnvironmentEnum["Staging"] = "qa";
})(MyTrumpfEnvironmentEnum || (MyTrumpfEnvironmentEnum = {}));
var MyTrumpfNotificationSeverityEnum;
(function (MyTrumpfNotificationSeverityEnum) {
    MyTrumpfNotificationSeverityEnum["Error"] = "ERROR";
    MyTrumpfNotificationSeverityEnum["Information"] = "INFORMATION";
    MyTrumpfNotificationSeverityEnum["Warning"] = "WARNING";
})(MyTrumpfNotificationSeverityEnum || (MyTrumpfNotificationSeverityEnum = {}));

const NO_TOKEN_ERROR = {
    status: 401,
    message: "no_token"
};
class MyTrumpfNotificationBackend {
    static getInstance() {
        return MyTrumpfNotificationBackend._instance;
    }
    constructor(options = {}) {
        this._categories = [];
        this._pollIncrement = 0;
        this.debug = false;
        this._log = (msg) => {
            if (this.debug && typeof console !== "undefined" && typeof console.log === "function") {
                /* eslint-disable-next-line no-console */
                console.log(`[MyTrumpfNotificationBackend]: ${msg}`);
            }
        };
        this.handleI18n = ({ language }) => {
            this._log(`language changed: "${language}"`);
            this.startPoll();
        };
        this.handleAuthClient = () => {
            this._log("token changed");
            this.startPoll();
        };
        this.updateCategories = async () => {
            const response = await fetchCategories({
                stage: this._environment,
                token: this._authClient.getAccessToken(),
                locale: i18nProvider.language
            });
            if (response.ok) {
                const data = await response.json();
                this._categories = data.categories;
            }
            else {
                this._categories = [];
            }
        };
        this.startPoll = () => {
            window.clearTimeout(this._pollTimeout);
            if (this._provider === undefined) {
                this._log("[SKIP] polling: no provider");
                return;
            }
            // reset notification provider state
            this._provider.setError(null);
            this._provider.setNotifications([]);
            this._provider.setPending(true);
            const token = this._authClient.getAccessToken();
            if (token === null) {
                this._log("[SKIP] polling: no token");
                this._provider.setError(NO_TOKEN_ERROR);
                this._provider.setPending(false);
                return;
            }
            this.updateCategories().then(() => {
                this._log("start polling …");
                this.poll();
            });
        };
        this.poll = async () => {
            var _a;
            window.clearTimeout(this._pollTimeout);
            const provider = this._provider;
            const token = this._authClient.getAccessToken();
            const { language } = i18nProvider;
            if (!provider) {
                this._log("[SKIP] poll: no provider");
                return;
            }
            if (token === null) {
                this._log("[SKIP] request: no token");
                provider.setNotifications([]);
                provider.setError(NO_TOKEN_ERROR);
                provider.setPending(false);
                return;
            }
            provider.setError(null);
            provider.setPending(true);
            this._pollIncrement++;
            const pollIncrement = this._pollIncrement;
            this._log(`request #${pollIncrement} (locale: "${language}" stage: "${this._environment}")`);
            const response = await fetchNotifications({
                stage: this._environment,
                token,
                locale: language
            });
            if (pollIncrement !== this._pollIncrement || provider !== this._provider || token !== this._authClient.getAccessToken()) {
                this._log(`[SKIP] ignore response #${pollIncrement}`);
                return;
            }
            if (response.ok) {
                const data = await response.json();
                this._log(`response #${pollIncrement} ok (${(_a = data.notifications) === null || _a === void 0 ? void 0 : _a.length} notifications)`);
                const notifications = Array.isArray(data.notifications)
                    ? data.notifications.map((notification) => mapNotification(notification, this._categories))
                    : [];
                provider.setNotifications(notifications);
            }
            else {
                this._log(`response #${pollIncrement} not ok (status: ${response.status} statusText: "${response.statusText}")`);
                provider.setError({
                    status: response.status,
                    message: response.statusText
                });
            }
            provider.setPending(false);
            if (response.ok) {
                this._log(`polling again in ${this._pollInterval} ms …`);
                this._pollTimeout = window.setTimeout(this.poll, this._pollInterval);
            }
            else {
                this._log(`stop polling`);
            }
        };
        this.init(options);
    }
    get authClient() {
        return this._authClient;
    }
    get environment() {
        return this._environment;
    }
    get pollInterval() {
        return this._pollInterval;
    }
    get provider() {
        return this._provider;
    }
    init(options = {}) {
        if (MyTrumpfNotificationBackend._instance !== null) {
            MyTrumpfNotificationBackend._instance.destroy();
        }
        MyTrumpfNotificationBackend._instance = this;
        this.debug = options.debug || false;
        this._log("initialized");
        this._environment = options.environment || MyTrumpfEnvironmentEnum.Production;
        this._i18nSubscriber = i18nProvider.subscribe(this.handleI18n);
        this._authClient = getAuthClient(options.authClientName);
        this._authClient.subscribe(this.handleAuthClient);
        this._pollInterval =
            typeof options.pollInterval === "number" && !Number.isNaN(options.pollInterval)
                ? Math.max(options.pollInterval, NOTIFICATIONS_POLL_INTERVAL_MIN)
                : NOTIFICATIONS_POLL_INTERVAL_DEFAULT;
    }
    destroy() {
        if (MyTrumpfNotificationBackend._instance === this) {
            MyTrumpfNotificationBackend._instance = null;
            this._log("destroyed");
        }
        i18nProvider.unsubscribe(this._i18nSubscriber);
        this._authClient.unsubscribe(this.handleAuthClient);
        this._provider = undefined;
    }
    initProvider(provider) {
        if (this._provider !== undefined) {
            return;
        }
        this._log(`init provider`);
        this._provider = provider;
        this.startPoll();
    }
}
MyTrumpfNotificationBackend._instance = null;
const initMyTrumpfNotificationBackend = (options) => {
    const backend = new MyTrumpfNotificationBackend(options);
    registerNotificationBackend(backend, NotificationProviderScope.MyTrumpf);
    return backend;
};

export { NotificationProviderScope as N, UxNotificationSeverityEnum as U, UxNotificationStatusEnum as a, NotificationProvider as b, getNotificationProvider as g, initMyTrumpfNotificationBackend as i, registerNotificationBackend as r };

//# sourceMappingURL=backend-8895d157.js.map